import * as THREE from "three";
import { LIGHT, RUNTIME } from "./config";

const direction = (azimuthDegrees: number, elevationDegrees: number): THREE.Vector3 => {
  const azimuth = THREE.MathUtils.degToRad(azimuthDegrees);
  const elevation = THREE.MathUtils.degToRad(elevationDegrees);
  return new THREE.Vector3(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.max(0.06, Math.sin(elevation)),
    Math.cos(elevation) * Math.cos(azimuth),
  );
};

export interface Lighting {
  sun: THREE.DirectionalLight;
  ambient: THREE.AmbientLight;
  hemisphere: THREE.HemisphereLight;
  focus: THREE.Vector3;
  setSun(azimuth: number, elevation: number): void;
  requestUpdate(): void;
}

export function createLighting(scene: THREE.Scene): Lighting {
  const ambient = new THREE.AmbientLight(0xffffff, LIGHT.ambient);
  scene.add(ambient);

  const hemisphere = new THREE.HemisphereLight(0xffffff, 0x8f8d88, LIGHT.hemisphere);
  scene.add(hemisphere);

  const sun = new THREE.DirectionalLight(0xffffff, LIGHT.intensity);
  sun.castShadow = true;
  const shadowSize = RUNTIME.lite ? 1024 : LIGHT.shadowMapSize;
  sun.shadow.mapSize.set(shadowSize, shadowSize);
  sun.shadow.bias = LIGHT.shadowBias;
  sun.shadow.normalBias = LIGHT.shadowNormalBias;

  const camera = sun.shadow.camera;
  camera.left = -LIGHT.shadowExtent;
  camera.right = LIGHT.shadowExtent;
  camera.top = LIGHT.shadowExtent;
  camera.bottom = -LIGHT.shadowExtent;
  camera.near = 1;
  camera.far = 160;
  camera.updateProjectionMatrix();

  scene.add(sun);
  scene.add(sun.target);

  let dir = direction(LIGHT.azimuth, LIGHT.elevation);
  const focus = new THREE.Vector3();

  const apply = (): void => {
    sun.target.position.copy(focus);
    sun.target.updateMatrixWorld();
    sun.position.copy(focus).addScaledVector(dir, 90);
    sun.shadow.camera.updateProjectionMatrix();
    sun.shadow.needsUpdate = true;
  };

  apply();

  return {
    sun,
    ambient,
    hemisphere,
    focus,
    setSun: (azimuth: number, elevation: number): void => {
      dir = direction(azimuth, elevation);
      apply();
    },
    requestUpdate: apply,
  };
}
